import React, { useState } from "react";
import moment from "moment";
import { ReactComponent as BackIcon } from "assets/icons/back.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { Button } from "components/buttons";
import { Input } from "components/input/Input";

import { useMutation, useQuery } from "@apollo/client";
import {
  COUPONS,
  LOAD_SAVINGSACCOUNT,
  LOAD_PROFILE_INFO,
} from "GraphQL/Queries";
import {
  TRIPVAULT_MUTATION,
  RESEND_EMAIL_VERIFICATION_MUTATION,
} from "GraphQL/Mutations";
import { ReactComponent as InfoSquare } from "assets/icons/info-square.svg";

import vaultStore from "stores/vaultStore";

import { ReactComponent as InfoFill } from "assets/icons/info-fill.svg";
import { closestNumber, formatter, getNumberOfDays } from "utils/helpers";
import { useForm } from "react-hook-form";
import {
  errorToast,
  buttonToast,
  warningToast,
  successToast,
} from "components/toast/toast";
import { Link } from "react-router-dom";

const CreateNewTripVault = ({
  onClose,
  packageDetails,
  goBack,
  rate,
  numberOfPackages,
  departureDate,
  selectedRoomRateId,
  setView,
  longTerm_deadline_date,
  setVaultModal,
  setPaymentType,
  selectedCoupon,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const profile = useQuery(LOAD_PROFILE_INFO);

  const { setPaying, setExactTrip, exactTrip, setPaymentCouponCode } =
    vaultStore;

  const { control, handleSubmit, watch, register } = useForm({
    defaultValues: {
      savingFrequency: "",
    },
  });

  const savingFrequencies = [
    { name: "Daily", days: "(1 Day)" },
    { name: "Weekly", days: "(7 Days)" },
    { name: "Monthly", days: "(30 Days)" },
  ];

  const frequency = watch("savingFrequency");
  const intervalValue = watch("debit_interval");

  const debitInterval =
    frequency === "Daily"
      ? "1"
      : frequency === "Weekly"
      ? "7"
      : frequency === "Monthly"
      ? "30"
      : frequency === ""
      ? intervalValue
      : "0";

  const frequencyAmount =
    (debitInterval /
      closestNumber(
        getNumberOfDays(
          packageDetails?.duration_type === "LONG_TERM"
            ? longTerm_deadline_date
            : packageDetails?.booking_deadline_date,
        ),
        debitInterval,
      )) *
    rate;

  const packageFrequencyRate =
    frequencyAmount > rate
      ? formatter.format(rate)
      : formatter.format(frequencyAmount);

  const [createVault] = useMutation(TRIPVAULT_MUTATION, {
    refetchQueries: [{ query: LOAD_SAVINGSACCOUNT }, { query: COUPONS }],
  });

  const [resendVerification, { isLoading: verifyLoading }] = useMutation(
    RESEND_EMAIL_VERIFICATION_MUTATION,
  );

  const handleVerify = async () => {
    try {
      const response = await resendVerification({
        variables: {
          email: profile?.data?.me?.user?.email,
        },
      });

      if (response?.data?.resendEmailVerification?.__typename === "Success") {
        successToast(
          "Success",
          response?.data?.resendEmailVerification?.message,
        );
      } else {
        errorToast("Error", response?.data?.resendEmailVerification?.message);
      }
    } catch (error) {
      console.log({ error });
    }
  };

  const createTripVault = handleSubmit(async (data) => {
    const payload = {
      package: packageDetails.id,
      autosave: false,
      group_type: "PERSONAL",
      debit_interval: parseInt(debitInterval),
      target_date:
        packageDetails?.duration_type === "LONG_TERM"
          ? longTerm_deadline_date
          : packageDetails?.booking_deadline_date,
      travel_start_date:
        packageDetails?.duration_type === "LONG_TERM"
          ? departureDate
          : packageDetails?.departure_date,
      targetAmount: rate,
      savings_frequency: frequency ? frequency.toUpperCase() : "CUSTOM",
      savings_type: "PACKAGE",
      room_rate_id: selectedRoomRateId,
      joint_user_ids: [],
      number_adults: packageDetails?.number_adults,
      number_children: packageDetails?.number_children || 0,
      number_infants: packageDetails.number_infants || 0,
      number_packages: parseInt(numberOfPackages),
      payment_type: "INSTALLMENT",
      couponCode: selectedCoupon?.couponCode,
    };

    setSubmitting(true);

    try {
      const response = await createVault({
        variables: payload,
      });

      if (
        response.data?.addTripVault.__typename === "AddSavingsAccountSuccess"
      ) {
        setPaymentType("vault");
        setExactTrip(response.data.addTripVault.savingsAccount);
        setSubmitting(false);
        setSuccess(true);
      } else if (
        response.data.addTripVault.__typename === "BadRequest" &&
        response.data.addTripVault.message === "Un-verified user!"
      ) {
        setSubmitting(false);
        buttonToast(
          "Action Required",
          response.data.addTripVault.message,
          "Verify Now",

          () => handleVerify(),
          "warning",
        );
      } else {
        setSubmitting(false);
        warningToast("Warning", response.data.addTripVault.message);
      }
    } catch (error) {
      setSubmitting(false);
      setSuccess(false);
      //   setPaying(false);
      if (!data.savingFrequency) {
        errorToast("Error", "Select savings frequency");
      } else {
        errorToast("Error", error ? error?.message : "An error occured");
      }
    }
  });

  const startPayment = () => {
    setVaultModal(null);
    setView(2);
    setPaymentType("vault");
  };

  return (
    <div className="w-full lg:w-[540px]  h-full bg-white-100 rounded-lg">
      <div className="border-b border-white-soft flex items-center justify-between py-4 px-5">
        <div className="flex items-center gap-x-2">
          <button onClick={goBack}>
            <BackIcon />
          </button>

          <span className="font-bold text-base text-gray-500">
            Create a personal trip vault{" "}
          </span>
        </div>

        <button onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <section className="px-4 lg:px-6 py-4">
        <div className="rounded-lg p-2 bg-white-neutral">
          <ul className="bg-white-100 px-3">
            <li className="border-b border-white-soft py-3 ">
              <span className="text-gray-500 text-base font-medium block">
                Package Name
              </span>
              <span className="text-sm text-gray-500">
                {" "}
                {packageDetails?.name}
              </span>
            </li>

            <li className=" py-3 ">
              <span className="text-gray-500 text-base font-medium block">
                Select a saving frequency{" "}
              </span>
              <span className="text-sm text-gray-500">
                Choose how you’d like to save towards your dream trip.{" "}
              </span>

              <div className="mt-2">
                <select
                  id="customSelect"
                  {...register("savingFrequency")}
                  className="custom-select"
                >
                  <option value="">Custom</option>
                  {savingFrequencies?.map((opt) => (
                    <option className="" key={opt.name} value={opt.name}>
                      {opt.name} {opt.name !== "Custom" && opt.days}
                    </option>
                  ))}
                </select>
                {frequency && (
                  <p>
                    <span className="text-xs text-blue-100 ">
                      {packageFrequencyRate} {frequency}
                    </span>
                  </p>
                )}

                {frequency === "" && (
                  <div className="mt-2">
                    {" "}
                    <Input
                      name="debit_interval"
                      placeholder={"Number of days between debits"}
                      type="number"
                      className="border border-[#E1E4EA] bg-white-100 rounded-lg w-full px-3 py-2"
                      inputClass="w-full"
                      register={register}
                      registerOptions={{ required: true }}
                      days={true}
                    />{" "}
                  </div>
                )}
              </div>
            </li>
          </ul>

          <ul className="bg-white-100 px-3 mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
            <li className="border-b border-white-soft py-3 ">
              <span className="uppercase text-gray-400 text-sm font-medium block">
                deadline date
              </span>
              <span className="text-base text-gray-500 font-medium">
                {packageDetails?.duration_type === "LONG_TERM"
                  ? longTerm_deadline_date &&
                    moment(longTerm_deadline_date).format("dddd, ll.")
                  : packageDetails?.booking_deadline_date &&
                    moment(packageDetails?.booking_deadline_date).format("ll.")}
              </span>
            </li>

            <li className=" py-3 border-b border-white-soft">
              <span className="uppercase text-gray-400 text-sm font-medium block">
                departure date
              </span>
              <span className="text-base text-gray-500 font-medium">
                {moment(departureDate).format("ll.")}
              </span>
            </li>
            <li className=" py-3  lg:col-span-2">
              <span className="uppercase text-gray-400 text-sm font-medium block">
                target amount
              </span>
              <span className="text-base text-gray-500 font-medium">
                {formatter.format(rate)}
              </span>
            </li>
          </ul>

          <div className="">
            <div
              className={`bg-[#E9F6FC] flex items-start gap-2 text-sm text-[#101C28] w-full p-2 sm:p-4`}
            >
              <InfoFill />
              <p className="flex-1">
                Secure this offer in your trip vault by adding at least{" "}
                <span className="text-blue-400">
                  {formatter.format(Math.ceil(rate * 0.3))}{" "}
                </span>{" "}
                in the next 6 hours!
              </p>
            </div>
          </div>

          <div className="mt-4">
            <Button
              disabled={submitting}
              onClick={createTripVault}
              isBlue
              text={submitting ? "Creating Vault" : "Create new vault "}
              className="text-sm !p-0 flex items-center justify-center flex-row-reverse gap-1"
            />
          </div>
        </div>
      </section>

      {success && (
        <div className=" py-8 px-8 bg-black-100/50 fixed top-0 left-0 w-full h-screen flex items-end lg:items-center lg:justify-center">
          <div className="z-[9999] w-full max-w-[540px] bg-white-100 rounded-lg  h-[350px] lg:h-[260px]">
            <section className="pt-4 rounded-lg mt-2 h-full text-center">
              <InfoSquare className="mx-auto" />

              <h4 className="text-base mt-4 text-[#1D2939] font-bold">
                Trip vault created
              </h4>

              <div className="text-sm text-[#525866]">
                <p className="">
                  You’ve successfully created a trip vault for your package.{" "}
                  <br />
                  Secure this offer by adding at least{" "}
                  <span className="text-blue-400">
                    {formatter.format(Math.ceil(rate * 0.3))}{" "}
                  </span>{" "}
                  <br /> within the next 6 hours!
                </p>
              </div>

              <div className="mt-4 border-t border-black-100/20 pt-4 px-4 flex flex-col lg:flex-row gap-4 lg:justify-end">
                <Link to="/tripvault">
                  <Button
                    className="border border-gray-50 !text-black-100 w-full lg:!w-fit px-4 py-1"
                    isWhite
                    text="View trip vault"
                  />
                </Link>

                <Button
                  onClick={startPayment}
                  className="w-full lg:!w-fit px-2 py-1"
                  isBlue
                  text="Okay, pay now"
                />
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateNewTripVault;
